/**
 * "Deals for you": vendor promotions on materials this buyer searches, views
 * or orders, shown before they start to everyone. Signed-in buyers only; the
 * vendor is never named. Hidden unless the buyer-care switch is on and the
 * buyer has not opted out.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Deal, loadDeals, useBuyerExperience } from "../../buyerCare";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";

const day = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const DealsForYou = () => {
  const experience = useBuyerExperience();
  const { authUser } = useFirebaseAuthentication();
  const [deals, setDeals] = useState<Deal[]>([]);

  useEffect(() => {
    if (!experience.earlyAccessDeals || !authUser) return;
    let alive = true;
    loadDeals().then((d) => alive && setDeals(d));
    return () => {
      alive = false;
    };
  }, [experience.earlyAccessDeals, authUser]);

  if (!experience.earlyAccessDeals || !authUser || !deals.length) return null;
  return (
    <div className="mx-auto mt-10 max-w-4xl px-6 text-left">
      <div className="text-sm font-semibold text-gray-700">
        Deals for you
        <span className="ml-2 text-xs font-normal text-gray-500">
          You see these before they open to everyone.
        </span>
      </div>
      <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {deals.map((d) => (
          <li
            key={d.promotionId}
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-gray-800 shadow-sm"
          >
            <div className="font-medium">
              {d.pct}% off {d.families.length ? d.families.join(", ") : "selected items"}
              {d.label ? ` - ${d.label}` : ""}
            </div>
            <div className="mt-0.5 text-xs text-gray-600">
              {day(d.startsAt)} to {day(d.endsAt)}, from {d.supplierLabel}. Prices
              switch on the start date.
            </div>
            {d.sample.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs">
                {d.sample.slice(0, 3).map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/product?id=${encodeURIComponent(s.id)}`}
                      className="text-[#00062e] underline"
                    >
                      {s.summary}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
